import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios from "../../utils/axios";
import {
  setError as setAppError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";

function toUTCDateString(date) {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  )
    .toISOString()
    .slice(0, 10);
}

export function getDefaultSalesRange() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);

  return [from, to];
}

export default function useMerchantSalesOverviewAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const clientRef = useRef(client);
  const [merchantSales, setMerchantSales] = useState(null);
  const [salesChart, setSalesChart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  clientRef.current = client;

  const getTotalSales = useCallback(async (dateRange) => {
    const [fromDate, toDate] = Array.isArray(dateRange) ? dateRange : [];
    if (!fromDate || !toDate) return null;

    setLoading(true);
    setError("");
    dispatch(setTableLoading({ key: "salesOverview", loading: true }));
    dispatch(setAppError(null));

    try {
      const response = await clientRef.current.post("/v1/Overview/GetTotalSales", {
        SessionID: sessionStorage.getItem("SessionID"),
        DateFrom: toUTCDateString(fromDate),
        DateTo: toUTCDateString(toDate),
      });

      const payload = response?.data?.Payload || {};
      setMerchantSales(payload.totalSalesRecords || []);
      setSalesChart(payload.MerchantSalesCharts || []);
      dispatch(
        setTableData({
          key: "salesOverview",
          data: payload.totalSalesRecords || [],
          pagination: {
            page: 1,
            totalPages: 1,
            totalCount: Array.isArray(payload.totalSalesRecords)
              ? payload.totalSalesRecords.length
              : 0,
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "summary",
          key: "salesOverview",
          value: {
            totalSalesRecords: payload.totalSalesRecords || [],
            merchantSalesCharts: payload.MerchantSalesCharts || [],
            dateFrom: toUTCDateString(fromDate),
            dateTo: toUTCDateString(toDate),
          },
        }),
      );
      return payload;
    } catch (requestError) {
      setMerchantSales([]);
      setSalesChart([]);
      setError("Unable to load sales overview right now.");
      dispatch(
        setTableData({
          key: "salesOverview",
          data: [],
          pagination: {
            page: 1,
            totalPages: 0,
            totalCount: 0,
          },
        }),
      );
      dispatch(
        setAppError(
          requestError?.response?.data?.Message ||
            requestError?.response?.data?.message ||
            "Unable to load sales overview right now.",
        ),
      );
      throw requestError;
    } finally {
      setLoading(false);
      dispatch(setTableLoading({ key: "salesOverview", loading: false }));
    }
  }, [dispatch]);

  return {
    error,
    getTotalSales,
    loading,
    merchantSales,
    salesChart,
  };
}
